import React, { useEffect, useState } from "react";
import { PodcastRequestCard } from "../../components/PodcastRequestCard";
import { SponsoredVendors } from "../../components/social/SponsoredVendors";
import { WeddingJourney } from "../../components/dashboard/WeddingJourney";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check, Circle, Users, Mail, Image as ImageIcon, Calendar, Sparkles,
  ArrowRight, Heart, ChevronRight,
} from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import CountdownCard from "../../components/CountdownCard";
import ProHome from "./ProHome";

export default function DashboardHome({ viewAs }) {
  const { user } = useAuth();
  return ["vendor", "planner"].includes(user?.role) || viewAs === "planner" ? <ProHome /> : <CoupleHome />;
}

function CoupleHome() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    // Therapists land on the therapy hub by default
    if (user?.role === "therapist") {
      nav("/dashboard/therapy", { replace: true });
      return;
    }
    api.get("/dashboard/summary").then((r) => {
      if (!r.data.has_wedding) {
        // Admins without a wedding stay on the dashboard summary (empty state)
        if (user?.role === "admin") {
          setSummary({ has_wedding: false, admin_empty: true });
        } else {
          nav("/onboarding");
        }
      } else {
        setSummary(r.data);
      }
    }).catch(() => {});
  }, [nav, user]);

  if (!summary) {
    return <div className="text-center py-20"><div className="w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin mx-auto" /></div>;
  }

  // Clean admin empty-state — no wedding linked
  if (summary.admin_empty) {
    return (
      <div data-testid="dashboard-home-admin-empty" className="space-y-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Admin</p>
          <h1 className="font-serif font-light text-4xl">Welcome back</h1>
          <p className="text-[var(--muted)] text-sm mt-2 max-w-xl">
            You're the platform admin. Use the sidebar to manage couples, therapists, agreements, the wedding contest, podcast, and built-in live video.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { to: "/dashboard/admin/users", label: "Users & Plans", desc: "Search, manage roles, plans." },
            { to: "/dashboard/admin/therapy", label: "Therapy", desc: "Applications, sessions, branding." },
            { to: "/dashboard/admin/agreements", label: "Agreements", desc: "Send, void, audit, sign-link." },
            { to: "/contest", label: "Wedding Contest", desc: "$25K contest leaderboard and winner controls." },
            { to: "/dashboard/admin/podcast", label: "Podcast Studio", desc: "Schedule live shows + uploads." },
            { to: "/dashboard/admin/blog", label: "Blog Editor", desc: "SEO posts, AI-assisted." },
          ].map((c) => (
            <Link key={c.to} to={c.to} className="bg-white border border-[var(--line)] rounded-2xl p-5 hover:border-[var(--gold)] transition-colors block group" data-testid={`admin-quicklink-${c.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <p className="font-serif text-2xl mb-1">{c.label}</p>
              <p className="text-xs text-[var(--muted)]">{c.desc}</p>
              <span className="text-[var(--gold)] text-xs mt-3 inline-flex items-center gap-1 group-hover:gap-2 transition-all">Open <ChevronRight className="w-3 h-3" /></span>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  const { wedding, stats, recent_memories } = summary;
  const tasks = [
    { label: "Choose your website template", done: true },
    { label: "Send your save the dates", done: stats.invitations_count > 0 },
    { label: "Add ceremony and reception details", done: !!wedding.venue },
    { label: "Collect RSVP responses", done: false, value: `${stats.attending}/${stats.total_guests || 1}` },
    { label: "Track dress & tux sizes", done: false, value: "0/8" },
    { label: "Upload engagement photos", done: stats.memories_count > 0, value: stats.memories_count > 0 ? "" : "0/1" },
  ];

  return (
    <div data-testid="dashboard-home">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-script text-[var(--gold)] text-3xl mb-1">Welcome back</p>
          <h1 className="font-serif font-light text-4xl md:text-5xl">{wedding.couple_names}</h1>
          {wedding.wedding_date && <p className="text-[var(--muted)] mt-2">{new Date(wedding.wedding_date).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>}
        </div>
        <Link to={`/site/${wedding.slug}`} target="_blank" className="btn-outline-gold" data-testid="view-site-link">
          View Public Site <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <WeddingJourney />

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { icon: Users, label: "Total Guests", v: stats.total_guests, to: "/dashboard/guests" },
          { icon: Check, label: "Attending", v: stats.attending, to: "/dashboard/rsvps" },
          { icon: ImageIcon, label: "Memories", v: stats.memories_count, to: "/dashboard/memories" },
          { icon: Mail, label: "Invitations", v: stats.invitations_count, to: "/dashboard/invitations" },
        ].map((s, i) => (
          <Link to={s.to} key={i} className="bg-white border border-[var(--line)] rounded-2xl p-5 hover:border-[var(--gold)] transition-colors group">
            <s.icon className="w-5 h-5 text-[var(--gold)] mb-3" />
            <p className="font-serif text-3xl">{s.v}</p>
            <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] mt-1">{s.label}</p>
          </Link>
        ))}
      </div>

      {/* Interactive features spotlight */}
      <div className="grid md:grid-cols-3 gap-4 mb-8" data-testid="dashboard-games-spotlight">
        <Link to="/dashboard/locked-in" className="group bg-gradient-to-br from-[var(--ink)] to-black text-white rounded-2xl p-6 hover:shadow-2xl transition-all">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Signature Game</p>
          <p className="font-serif text-2xl mb-1">Locked In Questions</p>
          <p className="text-xs text-white/60 mb-4">Host trivia for your guests with a live leaderboard.</p>
          <span className="inline-flex items-center gap-1 text-sm text-[var(--gold)] group-hover:gap-2 transition-all">Open hub <ChevronRight className="w-4 h-4" /></span>
        </Link>
        <Link to="/dashboard/compatibility" className="group bg-white border border-[var(--line)] rounded-2xl p-6 hover:border-[var(--gold)] transition-colors">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Quiz</p>
          <p className="font-serif text-2xl mb-1">Compatibility Test</p>
          <p className="text-xs text-[var(--muted)] mb-4">Discover your love archetype together.</p>
          <span className="inline-flex items-center gap-1 text-sm text-[var(--gold)] group-hover:gap-2 transition-all">Take the test <ChevronRight className="w-4 h-4" /></span>
        </Link>
        <Link to="/dashboard/announcements" className="group bg-white border border-[var(--line)] rounded-2xl p-6 hover:border-[var(--gold)] transition-colors">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Buzz</p>
          <p className="font-serif text-2xl mb-1">Drop an Announcement</p>
          <p className="text-xs text-[var(--muted)] mb-4">Get Locked In cards for your engagement, save-the-date and more.</p>
          <span className="inline-flex items-center gap-1 text-sm text-[var(--gold)] group-hover:gap-2 transition-all">Create one <ChevronRight className="w-4 h-4" /></span>
        </Link>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Tasks */}
        <div className="lg:col-span-2 bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">What's Next</p>
            <Link to="/dashboard/timeline" className="text-xs text-[var(--muted)] hover:text-[var(--gold)] flex items-center gap-1">View timeline <ChevronRight className="w-3 h-3" /></Link>
          </div>
          <div className="space-y-3">
            {tasks.map((t, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-[var(--line)] last:border-0">
                {t.done ? (
                  <div className="w-5 h-5 rounded-full bg-[var(--gold)] flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>
                ) : (
                  <Circle className="w-5 h-5 text-[var(--muted)]" />
                )}
                <span className={`flex-1 text-sm ${t.done ? "line-through text-[var(--muted)]" : ""}`}>{t.label}</span>
                {t.value && <span className="text-xs px-2.5 py-1 rounded-full bg-[var(--bg-warm)] text-[var(--ink)]">{t.value}</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Countdown card */}
        <div className="bg-[var(--bg-warm)] rounded-2xl p-6 flex items-center justify-center">
          {wedding.wedding_date ? (
            <CountdownCard names={wedding.couple_names} date={wedding.wedding_date} />
          ) : (
            <div className="text-center">
              <Calendar className="w-8 h-8 mx-auto text-[var(--gold)] mb-3" />
              <p className="font-serif text-lg mb-3">Set your date</p>
              <Link to="/dashboard/website" className="btn-outline-gold text-xs py-2">Add wedding date</Link>
            </div>
          )}
        </div>
      </div>

      {/* Recent memories */}
      <div className="mt-6 bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">Recent Memories</p>
          <Link to="/dashboard/memories" className="text-xs text-[var(--muted)] hover:text-[var(--gold)] flex items-center gap-1">Memory Vault <ChevronRight className="w-3 h-3" /></Link>
        </div>
        {recent_memories.length === 0 ? (
          <div className="text-center py-8">
            <Heart className="w-8 h-8 mx-auto text-[var(--gold)] mb-3" />
            <p className="text-sm text-[var(--muted)] mb-4">No memories yet. Upload your engagement photos to start your forever archive.</p>
            <Link to="/dashboard/memories" className="btn-gold">Upload Memories</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {recent_memories.map((m) => (
              <div key={m.memory_id || m.storage_path} className="aspect-square rounded-xl overflow-hidden bg-[var(--bg-warm)]">
                {m.content_type?.startsWith("image/") ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${m.storage_path}`} alt={m.caption} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[var(--muted)] text-xs">{m.content_type}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="mt-10"><SponsoredVendors place="couple_dashboard" title="Vendors you might love" /></div>
      <div className="mt-10"><PodcastRequestCard /></div>
    </div>
  );
}
